import events from "./events.js";

// Initialize global app data store if it doesn't exist
if (!window.appData) {
  window.appData = {
    lessons: [],
    activities: [],
    projects: [],
    devices: [],
    sensors: []
  };
}

// Centralized App Router for Smartkits (bundled)
class AppRouter {
  constructor() {
    this.currentState = {
      tab: null,
      section: null,
      id: null,
      view: null,
      params: {},
    };

    this.stateHandlers = new Map();
    this.pendingNavigation = null;

    this.initializeRouting();
  }

  initializeRouting() {
    this.registerStateHandlers();

    // route is provided by riot-route CDN and exposed globally
    if (typeof route !== "undefined") {
      route(this.handleRoute.bind(this));
      // Handle browser back/forward
      window.addEventListener("popstate", this.handlePopState.bind(this));
    }
  }

  registerStateHandlers() {
    this.stateHandlers.set("lessons", {
      canHandle: (id) => this.isDataLoaded("lessons"),
      handle: (id, view, params) => this.handleLessonsRoute(id, view, params),
      waitFor: "lessons_loaded",
    });

    this.stateHandlers.set("activities", {
      canHandle: (id) => this.isDataLoaded("activities"),
      handle: (id, view, params) =>
        this.handleActivitiesRoute(id, view, params),
      waitFor: "activities_loaded",
    });

    this.stateHandlers.set("projects", {
      canHandle: (id) => this.isDataLoaded("projects"),
      handle: (id, view, params) => this.handleProjectsRoute(id, view, params),
      waitFor: "projects_loaded",
    });

    this.stateHandlers.set("devices", {
      canHandle: (id) => this.isDataLoaded("devices"),
      handle: (id, view, params) => this.handleDevicesRoute(id, view, params),
      waitFor: "devices_loaded",
    });
  }

  handleRoute(section, id, view) {
    const query =
      typeof route !== "undefined" && route.query ? route.query() : {};

    this.currentState = { tab: section, section, id, view, params: query };

    if (!section || section === "") {
      this.navigateTo("lessons");
      return;
    }

    if (
      section &&
      (section.startsWith("access_token") ||
        section.startsWith("id_token") ||
        section.startsWith("token"))
    ) {
      return;
    }

    if (this.stateHandlers.has(section)) {
      const handler = this.stateHandlers.get(section);
      if (handler.canHandle(id)) {
        handler.handle(id, view, query);
      } else {
        this.pendingNavigation = { section, id, view, params: query };
        const handleDataLoaded = () => {
          if (
            this.pendingNavigation &&
            this.pendingNavigation.section === section &&
            this.pendingNavigation.id === id
          ) {
            handler.handle(id, view, query);
            this.pendingNavigation = null;
          }
          events.off(handler.waitFor, handleDataLoaded);
        };
        events.on(handler.waitFor, handleDataLoaded);
      }
    } else {
      this.navigateTo("lessons");
      return;
    }

    events.emit("route_tab_changed", section);
  }

  handleLessonsRoute(id, view, params) {
    if (!id) {
      events.emit("navigate_to_lessons_list");
      return;
    }
    if (id === "build") {
      events.emit("navigate_to_lesson_build");
      return;
    }
    events.emit("navigate_to_lesson", { lesson_id: id, view, params });
  }

  handleActivitiesRoute(id, view, params) {
    if (!id) {
      events.emit("navigate_to_activities_list", params);
      return;
    }
    if (view === "step" && params.step) {
      events.emit(
        "navigate_to_activity_step",
        { act_ref: id, stepNumber: parseInt(params.step), params }
      );
      return;
    }
    if (params.step) {
      const extendedParams = Object.assign({}, params, {
        step: parseInt(params.step),
      });
      events.emit("navigate_to_activity", { act_ref: id, view, params: extendedParams });
      return;
    }
    events.emit("navigate_to_activity", { act_ref: id, view, params });
  }

  handleProjectsRoute(id, view, params) {
    if (!id) {
      events.emit("navigate_to_projects_list");
      return;
    }
    if (id === "build") {
      events.emit("navigate_to_project_build");
      return;
    }
    events.emit("navigate_to_project", { project_id: id, view, params });
  }

  handleDevicesRoute(id, view, params) {
    if (!id) {
      events.emit("navigate_to_devices_list");
      return;
    }
    events.emit("navigate_to_device", { device_id: id, view, params });
  }

  handlePopState() {
    if (typeof route !== "undefined" && route.exec) route.exec();
  }

  // Helper methods for checking data state
  isDataLoaded(section) {
    switch (section) {
      case "lessons":
        return (
          window.appData &&
          window.appData.lessons &&
          window.appData.lessons.length > 0
        );
      case "activities":
        return window.appData && window.appData.activities && window.appData.activitiesLoaded;
      case "projects":
        return window.appData && window.appData.projects;
      case "devices":
        return (
          window.appData &&
          window.appData.devices &&
          window.appData.devices.length >= 0
        );
      default:
        return true;
    }
  }

  // Public API for programmatic navigation
  navigateTo(section, id = null, view = null, params = {}) {
    let url = section;
    if (id) url += "/" + id;
    if (view) url += "/" + view;
    if (Object.keys(params).length > 0) {
      const queryString = Object.entries(params)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join("&");
      url += "?" + queryString;
    }
    if (typeof route !== "undefined") route(url);
  }

  // Get current state
  getCurrentState() {
    return { ...this.currentState };
  }

  // Update URL without triggering navigation
  updateURL(section, id = null, view = null, params = {}) {
    let url = section;
    if (id) url += "/" + id;
    if (view) url += "/" + view;
    if (Object.keys(params).length > 0) {
      const queryString = Object.entries(params)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join("&");
      url += "?" + queryString;
    }
    if (window.history && window.history.replaceState) {
      window.history.replaceState({}, "", "#" + url);
    }
    this.currentState = { section, id, view, params };
  }
}

// Global router instance and helpers
window.appRouter = new AppRouter();
window.routerHelpers = {
  navigate: (section, id, view, params) =>
    window.appRouter.navigateTo(section, id, view, params),
  syncURL: (section, id, view, params) =>
    window.appRouter.updateURL(section, id, view, params),
  getState: () => window.appRouter.getCurrentState(),
  buildURL: (section, id, view, params) => {
    let url = section;
    if (id) url += "/" + id;
    if (view) url += "/" + view;
    if (params && Object.keys(params).length > 0) {
      const queryString = Object.entries(params)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join("&");
      url += "?" + queryString;
    }
    return url;
  },
};
